/**
 * <ReportAIInsight /> — the AI narration panel that sits at the top
 * of every ReportPage per M3X.REPORTS §1.F.
 *
 * Tier dispatch:
 *   - sonnet:   the 4 dashboards + 18 cross-domain analytics — full
 *               narration with anomaly detection
 *   - haiku:    per-domain operational reports (~115) — single
 *               paragraph summary
 *   - template: compliance reports (14) — deterministic, no LLM
 *
 * The component owns the fetch lifecycle: skeleton during load,
 * narration once ready, graceful no-op when the backend returns an
 * empty string (which it never does — always falls back to the
 * template tier).
 *
 * Bilingual: the agent label + footer caption respect the active
 * i18n locale. Narration comes back from the backend in the
 * requested locale.
 */

import { Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  type InsightContext,
  type InsightResult,
  fetchReportInsight,
} from "../../lib/reports/ai-insight";
import type { ReportAgent } from "../../lib/reports/catalog";

export interface ReportAIInsightProps {
  context: InsightContext;
  /** Optional override: never call the backend, just render the
   * caller-provided narration. Useful for tests / debug. */
  narrationOverride?: string;
}

const AGENT_LABEL_KEYS: Record<ReportAgent, string> = {
  tashghil: "agents.tashghil",
  rawatib: "agents.rawatib",
  mutabaa: "agents.mutabaa",
  khidmat: "agents.khidmat",
};

export function ReportAIInsight({
  context,
  narrationOverride,
}: ReportAIInsightProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<{
    status: "loading" | "ready" | "error";
    insight?: InsightResult;
    error?: string;
  }>({ status: narrationOverride !== undefined ? "ready" : "loading" });

  useEffect(() => {
    if (narrationOverride !== undefined) {
      setState({
        status: "ready",
        insight: {
          narration: narrationOverride,
          tierUsed: context.tier,
          cached: false,
        },
      });
      return undefined;
    }
    let cancelled = false;
    fetchReportInsight(context)
      .then((insight) => {
        if (!cancelled) setState({ status: "ready", insight });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setState({ status: "error", error: String(e) });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    context.reportId,
    context.tier,
    context.locale,
    context.period,
    narrationOverride,
  ]);

  const agentLabel = t(
    AGENT_LABEL_KEYS[context.agent] ?? "agents.tashghil",
    context.agent,
  );

  return (
    <section
      className="flex items-start gap-2 rounded-lg border border-ai-subtle bg-ai-bg px-4 py-3 text-sm text-text-secondary"
      aria-label={t("reports.ai_insight_label", "AI insight")}
    >
      <Sparkles
        size={14}
        className="mt-0.5 shrink-0 text-ai-deep"
        aria-hidden
      />
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-wide text-ai-deep/80">
          ✦ {agentLabel}
        </div>
        {state.status === "loading" && (
          <div className="mt-1 h-3 w-3/4 animate-pulse rounded bg-ai-bg/60" />
        )}
        {state.status === "ready" && state.insight && (
          <p className="mt-1 leading-relaxed">{state.insight.narration}</p>
        )}
        {state.status === "error" && (
          <p className="mt-1 text-xs text-text-tertiary italic">
            {t(
              "reports.ai_insight_unavailable",
              "AI insight unavailable — view the data below.",
            )}
          </p>
        )}
      </div>
    </section>
  );
}

export default ReportAIInsight;
